import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, setModal, setSelectedPost, toggleCommentDeleted } from '../../../../../Features/lensSlice';
import Comment from './Comment';
// import StarRating from '../../../../../Components/StarRating/StarRating';

import VerticalDot from '../../../../../Assets/VerticalDot.svg';
import Like from '../../../../../Assets/Lens/Like.svg';
import LikeGreen from '../../../../../Assets/Lens/LikeGreen.svg';
import CommentIcon from '../../../../../Assets/Lens/Comment.svg';
import Share from '../../../../../Assets/Lens/Share.svg';
import KebabMenu from './KebabMenu';
import { formatTimeDifference, formatDescription, getInitials, formatNumber, formatBadge, getCachedMediaUrl } from '../../../../../lib/utils/utils';
import { checkCommentingOnPost, checkUserLikeOnPost, getPostComments, getPostProfile, getProductByID, likePost, postComment, unlikePost } from '../../../../../lib/utils/apiCalls';
import { useOutsideClickForTwoRef } from '../../../../../Hooks/useOutsideClickForTwoRef';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { useLocation, useNavigate } from 'react-router-dom';

function PostContent({ post }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const kebabButtonRef = useRef(null);
    const kebabRef = useRef(null);

    useOutsideClickForTwoRef(kebabRef, kebabButtonRef, () => {
        setMenuVisible(false);
    });

    const commentDeleted = useSelector((state) => state.lens.commentDeleted);
    
    const [menuVisible, setMenuVisible] = useState(false);
    const [displayFullDescription, setDisplayFullDescription] = useState(false);
    
    const [product, setProduct] = useState({});
    const [comments, setComments] = useState([]);
    const [userProfile, setUserProfile] = useState({});

    const [inputValue, setInputValue] = useState('');
    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(0);

    const [commentAllowed, setCommentAllowed] = useState(false);

    const handleChange = (e) => {
        setInputValue(e.target.value);
    }

    const handleLikeClick = async () => {
        if(liked) {
            try{
                const likeCount = await unlikePost(post.id);
                setLikesCount(likeCount.noOfLikes);
            }
            catch(err) {
                console.log(err);
            }
        }
        else {
            try{
                const likeCount = await likePost(post.id);
                setLikesCount(likeCount.noOfLikes);
            }
            catch(err) {
                console.log(err);
            }
        }

        try{
            const response = await checkUserLikeOnPost(post.id);
            setLiked(response);
        }
        catch(err) {
            console.log(err);
        }
    }

    const handlePostComment = async () => {
        if(inputValue.trim()) {
            const callData = {
                postId: post.id,
                comment: inputValue,
            }
            try {
                const response = await postComment(callData);
                if(response?.status === 200) {
                    setInputValue('');
                    setComments([ ...comments, response.data.data ]);
                }
            }
            catch(err) {
                showToast(`${err}`, 'error');
            }
        }
    }

    const handleKeyDown = async (e) => {
        if(e.key === 'Enter') {
            await handlePostComment();
        }
    }

    const handleSharePost = () => {
        dispatch(setSelectedPost(post));
        dispatch(setModal('share'));
    }

    const handleOpenKebab = () => {
        setMenuVisible(!menuVisible);
    }

    const handleDescriptionLength = () => {
        setDisplayFullDescription(!displayFullDescription);
    }

    const handleUserClick = () => {
        const userId = post.UserId || post.SellerUserId;
        dispatch(closeModal());
        navigate(`${location.pathname}/profile/${userId}`);
    }

    useEffect(() => {
        setLikesCount(post.noOflikes);
        const userId = post.UserId || post.SellerUserId;

        const getProduct = async () => {
            const response = await getProductByID(post.productId);
            if(response?.status === 200) {
                setProduct(response?.data?.product);
            }
        }

        const getUserProfile = async () => {
            const profile = await getPostProfile(userId);
            setUserProfile(profile);
        }

        const getUserLiked = async () => {
            const response = await checkUserLikeOnPost(post.id);
            setLiked(response);
        }

        const getComments = async () => {
            const response = await getPostComments(post.id);
            setComments(response);
        }

        const checkIfCommentingAllowed = async () => {
            try {
                const response = await checkCommentingOnPost(post.id);
                setCommentAllowed(response);
            }
            catch(err) {
                console.log(err);
            }
        }

        (Object.keys(post).length > 0 && post.productId) && getProduct();
        Object.keys(post).length > 0 && getComments();
        Object.keys(post).length > 0 && getUserLiked();
        Object.keys(post).length > 0 && getUserProfile();
        Object.keys(post).length > 0 && checkIfCommentingAllowed();
    }, [post]);

    useEffect(() => {
        async function handleGetNewComments() {
            const newComments = await getPostComments(post.id);
            setComments(newComments);
        }

        if(commentDeleted) {
            handleGetNewComments();
            dispatch(toggleCommentDeleted());
        }
        // eslint-disable-next-line
    }, [commentDeleted]);

    function handleDirectToProduct() {
        dispatch(closeModal());
        navigate(`/inventory-management/product/${product.id}`);
    }

    return (
        <div className={`${(post?.imageUrls?.length > 0 || post.videoUrls) ? 'w-2/5' : 'w-full'} flex flex-col gap-4 h-full p-4 relative`}>
            <div className="flex flex-col gap-4 border-b border-[#999999]">
                <div className="flex flex-col gap-[5px]">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-1">
                            <div className="w-[50px] h-[50px] rounded-full overflow-hidden">
                                {
                                    userProfile?.ProfilePic ? 
                                    <img className='w-full h-full object-cover' src={getCachedMediaUrl(userProfile.ProfilePic)} alt="" /> : 
                                    <div className='w-full h-full bg-[#EAEAEA] flex items-center justify-center'>
                                        <p className='font-mulish select-none font-bold text-base text-[#666666]'>{getInitials(post.UserId ? userProfile.fullName : userProfile.name)}</p>
                                    </div>
                                }
                            </div>

                            <div className="flex flex-col">
                                <h1 onClick={handleUserClick} className='font-semibold text-[16px] leading-[26px] text-[#121212] select-none cursor-pointer hover:underline'>
                                    {post.UserId ? userProfile.fullName : userProfile.name}
                                </h1>

                                <p className='font-normal text-[14px] leading-6 text-[#808080] select-none'>
                                    {
                                        post.UserId ? 
                                        (userProfile.milestone ? `${userProfile.milestone === 'No Review' ? userProfile.milestone : `${formatBadge(userProfile.milestone)}`}` : '') : 
                                        `${userProfile.favourite ? formatNumber(userProfile.favourite) : '0'} Shoppers' Favorite`
                                    }
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <div className="flex items-center gap-1">
                                <div className="w-1 h-1 rounded-full bg-[#757575]"></div>

                                <p className='font-normal text-[12px] leading-6 text-[#A0A0A0]'>{formatTimeDifference(post.createdAt)} ago</p>
                            </div>

                            <div className="relative">
                                <button ref={kebabButtonRef} className='select-none outline-none border-none w-6 h-6 flex items-center justify-center' onClick={handleOpenKebab}>
                                    <img src={VerticalDot} alt="" />
                                </button>

                                <KebabMenu 
                                    boxRef={kebabRef} 
                                    user={userProfile} 
                                    post={post} 
                                    commentAllowed={commentAllowed} 
                                    handleCommentAllowed={(value) => setCommentAllowed(value)} 
                                    kebabOpen={menuVisible} 
                                    handleKebab={handleOpenKebab} 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-[2px]">
                        <p className='font-semibold text-[18px] leading-[30px]'>{post.postTitle}</p>
                    </div>

                    {
                        post?.postDesc?.length > 0 && 
                        <div className="relative">
                            <p className={`"flex-grow flex-wrap font-normal text-[14px] leading-[26px] ${post.postDesc.split(' ').length > 15 && 'mr-14'}`}>
                                {
                                    !displayFullDescription ? 
                                    (
                                        post.postDesc.split(' ').length > 15 ? 
                                        <span dangerouslySetInnerHTML={{ __html: formatDescription(post.postDesc.split(' ').slice(0, 15).join(' ') + '...') }} /> : 
                                        <span dangerouslySetInnerHTML={{ __html: formatDescription(post.postDesc) }} />
                                    )
                                    : <span dangerouslySetInnerHTML={{ __html: formatDescription(post.postDesc) }} />
                                }
                            </p>

                            {
                                post?.postDesc?.split(' ').length > 15 && 
                                <button onClick={handleDescriptionLength} className='hover:underline absolute bottom-0 right-0 font-semibold text-[14px] leading-[26px] text-[#A0A0A0]'>
                                    {
                                        displayFullDescription ? 
                                        'see less' : 
                                        'see more'
                                    }
                                </button>
                            }
                        </div>
                    }
                </div>

                {
                    Object.keys(product).length > 0 && 
                    <div onClick={handleDirectToProduct} className="w-full flex rounded-lg overflow-hidden cursor-pointer transition-all delay-75 ease-in hover:opacity-75">
                        <div className="w-[84px] h-[84px]">
                            {product.productImgUrls && product.productImgUrls[0] && (
                                <img className='w-full h-full object-cover' src={getCachedMediaUrl(product.productImgUrls[0])} alt="" />
                            )}
                        </div>

                        <div className="w-[calc(100%-84px)] bg-[#808080] p-1 flex flex-col justify-center">
                            <h1 className='font-medium text-[18px] leading-7 text-[#FFFFFF]'>{userProfile?.name}</h1>

                            <p className='w-full font-normal text-base text-[#FFFFFF] whitespace-nowrap overflow-hidden text-ellipsis'>{product?.productName}{product?.productName}</p>
                        </div>
                    </div>
                }

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            <button onClick={handleLikeClick} className="w-11 h-11 flex items-center justify-center">
                                <img src={liked ? LikeGreen : Like} alt="" />
                            </button>

                            {likesCount && <p className='select-none font-normal text-[14px] leading-[26px] text-[#121212]'>{formatNumber(likesCount)}</p>}
                        </div>

                        <div className="flex items-center cursor-pointer">
                            <div className="w-11 h-11 flex items-center justify-center">
                                <img src={CommentIcon} alt="" />
                            </div>

                            <p className='select-none font-normal text-[14px] leading-[26px] text-[#121212]'>{comments.length} Comment{comments?.length > 1 ? 's' : ''}</p>
                        </div>
                    </div>

                    <button onClick={handleSharePost} className="w-12 h-11 flex items-center justify-center">
                        <img src={Share} alt="" />
                    </button>
                </div>
            </div>

            <div className="overflow-y-scroll flex flex-col gap-[10px] pb-16 no-scrollbar">
                {
                    [ ...comments ]
                    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                    .map(comment => {
                        return (
                            <Comment 
                                key={comment.id} 
                                comment={comment} 
                                postId={post.id} 
                                postCommentAllow={commentAllowed} 
                            />
                        )
                    })
                }
            </div>

            {
                commentAllowed && 
                <div className="absolute px-4 py-3 flex items-center gap-2 bg-[#F5F5F5] bottom-0 left-0 w-full rounded-tl-[9px] rounded-tr-[9px] border border-[#EAEAEA]">
                    <input 
                        className='w-[calc(100%-44px)] bg-transparent outline-none' 
                        placeholder='Leave your thoughts here...' 
                        type="text" 
                        value={inputValue} 
                        onChange={handleChange} 
                        onKeyDown={handleKeyDown} 
                    />

                    <div className={`w-11 h-11 flex items-center justify-center`}>
                        <button onClick={handlePostComment} className={`${inputValue.length > 0 ? 'px-[10px] py-1 bg-primaryColor hover:bg-primaryAccent text-buttonText rounded' : 'text-[#999999]'} font-semibold text-[14px] leading-[26px] outline-none`}>Post</button>
                    </div>
                </div>
            }
        </div>
    );
}

export default PostContent;
